import { useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateChildrensLine,
  updateChildrensLevel,
  deleteEducationalStructuresDepartments,
} from '../../../actions/educationalStructure';

/**
 * Хук для управления древовидной структурой образовательных подразделений
 */
export const useEducationalTree = () => {
  const dispatch = useDispatch();
  const { staff } = useSelector((state) => state.educational_structure);
  const { teachers } = useSelector((state) => state.common);

  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState({});

  // Флаг блокировки: предотвращает повторные клики пока идёт запрос
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);

  /**
   * Обёртка для мутирующих операций — блокирует параллельные запросы
   */
  const withPending = useCallback(async (fn) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await fn();
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  }, []);
  /**
   * Преобразует плоский массив в иерархическую структуру для PrimeReact TreeTable
   */
  const buildTreeNodes = useCallback((flatData) => {
    if (!flatData?.length) return [];

    const map = new Map();

    // Создаём узлы с форматом PrimeReact
    flatData.forEach((item) => {
      map.set(item.did, {
        key: String(item.did),
        data: {
          ...item,
          teacherName: teachers?.find((t) => +t.mid === +item.mid)?.calc_fio || '',
        },
        children: [],
      });
    });

    const roots = [];

    // Строим иерархию
    flatData.forEach((item) => {
      const node = map.get(item.did);
      if (item.owner_did === 0) {
        roots.push(node);
      } else if (map.has(item.owner_did)) {
        map.get(item.owner_did).children.push(node);
      }
    });

    return roots;
  }, [teachers]);

  /**
   * Иерархия данных в формате PrimeReact TreeTable
   */
  const treeNodes = useMemo(() => {
    return buildTreeNodes(staff);
  }, [staff, buildTreeNodes]);

  /**
   * Найти узел по ключу (did)
   */
  const findNodeByKey = useCallback((nodes, key) => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children?.length) {
        const found = findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }, []);

  /**
   * Найти родительский узел
   */
  const findParentNode = useCallback((nodes, key, parent = null) => {
    for (const node of nodes) {
      if (node.key === key) return parent;
      if (node.children?.length) {
        const found = findParentNode(node.children, key, node);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  }, []);

  /**
   * Получить список соседей узла
   */
  const getSiblings = useCallback((key) => {
    const parent = findParentNode(treeNodes, key);
    return parent ? parent.children : treeNodes;
  }, [treeNodes, findParentNode]);

  /**
   * Получить индекс узла среди соседей
   */
  const getNodeIndex = useCallback((key) => {
    const siblings = getSiblings(key);
    return siblings.findIndex((n) => n.key === key);
  }, [getSiblings]);

  /**
   * Получить выбранный ключ из PrimeReact selection.
   * В TreeTable selectionMode='single' e.value — строка (ключ строки).
   */
  const getSelectedKey = useCallback(() => {
    if (!selectedNode) return null;
    // single mode → строка
    if (typeof selectedNode === 'string') return selectedNode;
    // на случай объектного формата { key: true }
    const keys = Object.keys(selectedNode);
    return keys.length > 0 ? keys[0] : null;
  }, [selectedNode]);

  /**
   * Переместить узел вверх (dispatch updateChildrensLine с 'up')
   */
  const moveUp = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return;

    const index = getNodeIndex(key);
    if (index > 0) {
      withPending(() => dispatch(updateChildrensLine(parseInt(key), 'up')));
    }
  }, [getSelectedKey, getNodeIndex, dispatch, withPending]);

  /**
   * Переместить узел вниз (dispatch updateChildrensLine с 'down')
   */
  const moveDown = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return;

    const siblings = getSiblings(key);
    const index = getNodeIndex(key);

    if (index < siblings.length - 1) {
      withPending(() => dispatch(updateChildrensLine(parseInt(key), 'down')));
    }
  }, [getSelectedKey, getSiblings, getNodeIndex, dispatch, withPending]);

  /**
   * Переместить узел на уровень ниже (сделать дочерним для предыдущего соседа)
   * Это "провал вниз" - downToNext в старом компоненте
   */
  const indentNode = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return;

    const siblings = getSiblings(key);
    const index = getNodeIndex(key);

    // Нужен предыдущий сосед чтобы стать его дочерним
    if (index > 0) {
      const newParentKey = siblings[index - 1].key;
      withPending(() => dispatch(updateChildrensLevel(parseInt(key), parseInt(newParentKey))));
    }
  }, [getSelectedKey, getSiblings, getNodeIndex, dispatch, withPending]);

  /**
   * Переместить узел на уровень выше (сделать соседом родителя)
   * Это "выход наверх" - upOutOfCurrentModule в старом компоненте
   */
  const outdentNode = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return;

    const parent = findParentNode(treeNodes, key);
    if (!parent) return; // Уже на верхнем уровне

    // Находим owner_did родителя (куда переместить)
    const grandParentOwnerDid = parent.data.owner_did;
    withPending(() => dispatch(updateChildrensLevel(parseInt(key), grandParentOwnerDid)));
  }, [getSelectedKey, treeNodes, findParentNode, dispatch, withPending]);

  /**
   * Удалить выбранный узел
   */
  const deleteNode = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return;

    const node = findNodeByKey(treeNodes, key);
    if (node) {
      withPending(async () => {
        await dispatch(deleteEducationalStructuresDepartments(node));
        setSelectedNode(null);
      });
    }
  }, [getSelectedKey, treeNodes, findNodeByKey, dispatch, withPending]);

  /**
   * Получить данные выбранного узла
   */
  const getSelectedNodeData = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return null;

    const node = findNodeByKey(treeNodes, key);
    return node?.data || null;
  }, [getSelectedKey, treeNodes, findNodeByKey]);

  /**
   * Количество прямых потомков выбранного узла (для предупреждения при удалении)
   */
  const getSelectedNodeChildrenCount = useCallback(() => {
    const key = getSelectedKey();
    if (!key) return 0;

    const node = findNodeByKey(treeNodes, key);
    return node?.children?.length || 0;
  }, [getSelectedKey, treeNodes, findNodeByKey]);

  /**
   * Проверка: можно ли переместить вверх
   */
  const canMoveUp = useMemo(() => {
    if (pending) return false;
    const key = getSelectedKey();
    if (!key) return false;
    return getNodeIndex(key) > 0;
  }, [pending, getSelectedKey, getNodeIndex]);

  /**
   * Проверка: можно ли переместить вниз
   */
  const canMoveDown = useMemo(() => {
    if (pending) return false;
    const key = getSelectedKey();
    if (!key) return false;

    const siblings = getSiblings(key);
    const index = getNodeIndex(key);
    return index < siblings.length - 1;
  }, [pending, getSelectedKey, getSiblings, getNodeIndex]);

  /**
   * Проверка: можно ли сделать провал вниз (indent)
   * Нужен предыдущий сосед и глубина < 10
   */
  const canIndent = useMemo(() => {
    if (pending) return false;
    const key = getSelectedKey();
    if (!key) return false;

    const index = getNodeIndex(key);
    if (index <= 0) return false;

    // Проверяем глубину
    let depth = 0;
    let parent = findParentNode(treeNodes, key);
    while (parent) {
      depth++;
      parent = findParentNode(treeNodes, parent.key);
    }

    return depth < 10;
  }, [pending, getSelectedKey, getNodeIndex, treeNodes, findParentNode]);

  /**
   * Проверка: можно ли сделать выход наверх (outdent)
   * Нужен родитель (не на верхнем уровне)
   */
  const canOutdent = useMemo(() => {
    if (pending) return false;
    const key = getSelectedKey();
    if (!key) return false;

    const parent = findParentNode(treeNodes, key);
    return parent !== null && parent !== undefined;
  }, [pending, getSelectedKey, treeNodes, findParentNode]);

  /**
   * Развернуть все узлы
   */
  const expandAll = useCallback(() => {
    const keys = {};
    const collectKeys = (nodes) => {
      nodes.forEach((node) => {
        if (node.children?.length) {
          keys[node.key] = true;
          collectKeys(node.children);
        }
      });
    };
    collectKeys(treeNodes);
    setExpandedKeys(keys);
  }, [treeNodes]);

  /**
   * Свернуть все узлы
   */
  const collapseAll = useCallback(() => {
    setExpandedKeys({});
  }, []);

  return {
    // Данные
    treeNodes,
    staff,
    teachers,

    // Состояние выбора
    selectedNode,
    setSelectedNode,
    getSelectedNodeData,
    getSelectedNodeChildrenCount,

    // Состояние раскрытия
    expandedKeys,
    setExpandedKeys,
    expandAll,
    collapseAll,

    // Операции с деревом
    moveUp,
    moveDown,
    indentNode,
    outdentNode,
    deleteNode,

    // Проверки
    canMoveUp,
    canMoveDown,
    canIndent,
    canOutdent,
    hasSelection: !pending && (
      typeof selectedNode === 'string'
        ? selectedNode.length > 0
        : selectedNode !== null && Object.keys(selectedNode || {}).length > 0
    ),

    // Состояние загрузки (блокировка кнопок)
    pending,

    // Утилиты
    findNodeByKey,
    findParentNode,
  };
};

export default useEducationalTree;
